import { signal } from '@angular/core';
import { LoggerService } from '../logger/logger.service';

/**
 * A storage service that stores values in a Storage instance of the browser.
 * @param T The type of the object save under the storagKey.
 * @param storageKey The key under which the values are stored in the Storage instance.
 * @param defaultValue The default vale that is used, if no value is found in the Storage instance.
 * @param storage The Storage instance to use (e.g. localStorage)
 */
export class GenericStorageService<T> {
	private logPrefix: string;
	private _value = signal<T | undefined>(undefined);

	constructor(
		private readonly storageKey: string,
		private readonly storage: Storage,
		private readonly defaultValue?: T,
	) {
		this.logPrefix = `[${this.storageKey}]`;
		const value = this.parseValueFromStorage() || this.defaultValue;
		this._value.set(value);
	}

	get value() {
		return this._value;
	}

	setValue(value: T | undefined) {
		this._value.set(value);
		this.save();
	}

	reset() {
		this._value.set(this.defaultValue);
		this.save();
	}

	private save() {
		try {
			this.storage.setItem(this.storageKey, JSON.stringify(this.value()));
			LoggerService.debug(this.logPrefix, 'updated to', { value: { ...this.value() } });
		} catch (error) {
			LoggerService.error(this.logPrefix, 'could not write to storage', {
				error,
				value: { ...this.value() },
			});
		}
	}

	private parseValueFromStorage() {
		try {
			const parsedValue = JSON.parse(this.storage.getItem(this.storageKey)!);
			LoggerService.debug(this.logPrefix, `value read from storage`, parsedValue);
			return parsedValue as T;
		} catch (_error) {
			LoggerService.error(this.logPrefix, `could not been read from storage. Using defaults.`, {
				value: { ...this.defaultValue },
			});
			return;
		}
	}
}
